import type * as lazyIndex from './lazyIndex';
import { LazyModule, LazyAction } from 'owa-bundling';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "SxSData" */ './lazyIndex'),
    { name: 'SxSData' }
);

export const lazyResetPreviewPane: LazyAction<
    typeof lazyIndex.resetPreviewPane,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.resetPreviewPane);
export const lazySetAudioPreviewState: LazyAction<
    typeof lazyIndex.setAudioPreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setAudioPreviewState);
export const lazySetCustomPreviewState: LazyAction<
    typeof lazyIndex.setCustomPreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setCustomPreviewState);
export const lazySetIframePreviewState: LazyAction<
    typeof lazyIndex.setIframePreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setIframePreviewState);
export const lazySetImagePreviewLoaded: LazyAction<
    typeof lazyIndex.setImagePreviewLoaded,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setImagePreviewLoaded);
export const lazySetImagePreviewState: LazyAction<
    typeof lazyIndex.setImagePreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setImagePreviewState);
export const lazySetImagePreviewStateFromImageGalleryItem: LazyAction<
    typeof lazyIndex.setImagePreviewStateFromImageGalleryItem,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setImagePreviewStateFromImageGalleryItem);
export const lazySetNativePdfPreviewState: LazyAction<
    typeof lazyIndex.setNativePdfPreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setNativePdfPreviewState);
export const lazySetPanesSize: LazyAction<typeof lazyIndex.setPanesSize, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.setPanesSize);
export const lazySetPreviewError: LazyAction<typeof lazyIndex.setPreviewError, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.setPreviewError);
export const lazySetPreviewLoading: LazyAction<
    typeof lazyIndex.setPreviewLoading,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setPreviewLoading);
export const lazySetRightPaneDisplayAndUpdateSize: LazyAction<
    typeof lazyIndex.setRightPaneDisplayAndUpdateSize,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setRightPaneDisplayAndUpdateSize);
export const lazySetSaveStatusText: LazyAction<
    typeof lazyIndex.setSaveStatusText,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setSaveStatusText);
export const lazySetTextPreviewState: LazyAction<
    typeof lazyIndex.setTextPreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setTextPreviewState);
export const lazySetVideoPreviewState: LazyAction<
    typeof lazyIndex.setVideoPreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setVideoPreviewState);
export const lazySetWacPreviewState: LazyAction<
    typeof lazyIndex.setWacPreviewState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setWacPreviewState);
export const lazySetWacReadOnly: LazyAction<typeof lazyIndex.setWacReadOnly, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.setWacReadOnly);
export const lazyUpdatePanesSize: LazyAction<typeof lazyIndex.updatePanesSize, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.updatePanesSize);
export const lazySaveHideReadingPaneOption: LazyAction<
    typeof lazyIndex.saveHideReadingPaneOption,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.saveHideReadingPaneOption);
export const lazySaveDefaultEditCommandOption: LazyAction<
    typeof lazyIndex.saveDefaultEditCommandOption,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.saveDefaultEditCommandOption);
export const lazySetMsPdfPasswordStateMutator: LazyAction<
    typeof lazyIndex.setMsPdfPasswordStateMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setMsPdfPasswordStateMutator);
export const lazySetMsPdfPrintDisabledStateMutator: LazyAction<
    typeof lazyIndex.setMsPdfPrintDisabledMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setMsPdfPrintDisabledMutator);
export const lazySetMsPdfPreviewStateMutator: LazyAction<
    typeof lazyIndex.setMsPdfPreviewStateMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setMsPdfPreviewStateMutator);
export const lazySetMsPdfPreviewStateByDataMutator: LazyAction<
    typeof lazyIndex.setMsPdfPreviewStateByDataMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setMsPdfPreviewStateByDataMutator);
export const lazyGetHideReadingPaneOption: LazyAction<
    typeof lazyIndex.getHideReadingPaneOption,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getHideReadingPaneOption);
export const lazyGetDefaultEditCommandOption: LazyAction<
    typeof lazyIndex.getDefaultEditCommandOption,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getDefaultEditCommandOption);

export { AutoHideEmailEvent } from './utils/eventNames';
export { MIN_PREVIEW_PANE_WIDTH, RESPONSIVE_READING_PANE_WIDTH } from './utils/sxsConstants';
export type { default as JsApiInfo } from './store/schema/JsApiInfo';
export type { default as LoadedInfo } from './store/schema/LoadedInfo';
export type { default as WacPreviewState } from './store/schema/WacPreviewState';
export type { default as VideoPreviewState } from './store/schema/VideoPreviewState';
export type { default as TextPreviewState } from './store/schema/TextPreviewState';
export { RightPaneDisplay } from './store/schema/SxSViewState';
export type { default as SxSViewState } from './store/schema/SxSViewState';
export { PreviewPaneMode } from './store/schema/PreviewPaneViewState';
export type {
    PreviewPaneErrorViewState,
    PreviewPaneLoadingViewState,
    PreviewPaneViewStateBase,
} from './store/schema/PreviewPaneViewState';
export { MsPdfPasswordState } from './store/schema/MsPdfPreviewState';
export type { default as MsPdfPreviewState } from './store/schema/MsPdfPreviewState';
export type { default as NativePdfPreviewState } from './store/schema/NativePdfPreviewState';
export type { default as ImagePreviewState } from './store/schema/ImagePreviewState';
export type { default as ImageGalleryItem } from './store/schema/ImageGalleryItem';
export type { ImageGalleryPromise } from './store/schema/ImageGalleryItem';
export type { default as IframePreviewState } from './store/schema/IFramePreviewState';
export type { default as AudioPreviewState } from './store/schema/AudioPreviewState';
export type { SxSCustomData } from './store/schema/SxSCustomData';
